import {
  Calendar,
  DollarSign,
  Globe,
  Mail,
  Phone,
  TrendingUp,
  User,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Reseller } from "../../components/types";
import { Link } from "react-router-dom";
import { sampleResellers } from "./data";

const Resellers: React.FC = ({}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("registrationDate");
  const [resellers, setResellers] = useState<Reseller[]>(sampleResellers);
  const userRole = localStorage.getItem("userRole");
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800";
      case "New":
        return "bg-blue-100 text-blue-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Inactive":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case "Gold":
        return "bg-yellow-100 text-yellow-800";
      case "Silver":
        return "bg-gray-100 text-gray-800";
      case "Bronze":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const filteredResellers = resellers
    .filter((reseller) => {
      const matchesSearch =
        reseller.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reseller.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reseller.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "All" || reseller.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === "registrationDate") {
        return (
          new Date(b.registrationDate).getTime() -
          new Date(a.registrationDate).getTime()
        );
      }
      if (sortBy === "totalSales") return b.totalSales - a.totalSales;
      if (sortBy === "name") return a.name.localeCompare(b.name);
      return 0;
    });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              {userRole === "global_admin" ? (
                <h1 className="text-2xl font-bold text-gray-900">
                  Resellers Management
                </h1>
              ) : (
                <h1 className="text-2xl font-bold text-gray-900">
                  Customers Management
                </h1>
              )}

              <p className="text-gray-600">
                Manage and monitor all registered{" "}
                {userRole === "global_admin" ? `resellers` : `customers`}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* <Link
                to={"/view-campaign"}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Back to Dashboard
              </Link> */}
              <Link
                to={"/add-reseller"}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Add New {userRole === "global_admin" ? `Reseller` : `Customer`}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="p-6">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total{" "}
                  {userRole === "global_admin" ? `Resellers` : `Customers`}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {resellers.length}
                </p>
              </div>
              <Users className="text-blue-600" size={24} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Active  {userRole === "global_admin" ? `Resellers` : `Customers`}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {resellers.filter((r) => r.status === "Active").length}
                </p>
              </div>
              <TrendingUp className="text-green-600" size={24} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  New This Month
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {resellers.filter((r) => r.status === "New").length}
                </p>
              </div>
              <Calendar className="text-purple-600" size={24} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Commission
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  ₦
                  {resellers
                    .reduce((sum, r) => sum + r.commission, 0)
                    .toLocaleString()}
                </p>
              </div>

              <span className="text-orange-600 text-[24px] font-bold">₦</span>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <Users
                  size={20}
                  className="absolute left-3 top-2.5 text-gray-400"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="All">All Statuses</option>
                <option value="Active">Active</option>
                <option value="New">New</option>
                <option value="Pending">Pending</option>
                <option value="Inactive">Inactive</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="registrationDate">
                  Sort by Registration Date
                </option>
                <option value="totalSales">Sort by Total Sales</option>
                <option value="name">Sort by Name</option>
              </select>
            </div>
            <div className="text-sm text-gray-600">
              Showing {filteredResellers.length} of {resellers.length} resellers
            </div>
          </div>
        </div>

        {/* Resellers Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reseller
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Territory
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registration
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResellers.map((reseller) => (
                  <tr
                    key={reseller.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User size={16} className="text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {reseller.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {reseller.company}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 text-sm text-gray-900">
                        <Mail size={14} className="text-gray-400" />
                        <span>{reseller.email}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Phone size={14} className="text-gray-400" />
                        <span>{reseller.phone}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            reseller.status
                          )}`}
                        >
                          {reseller.status}
                        </span>
                        <div>
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLevelColor(
                              reseller.level
                            )}`}
                          >
                            {reseller.level}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Globe size={14} className="text-gray-400 mr-2" />
                        {reseller.territory}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium">
                          ₦{reseller.totalSales.toLocaleString()}
                        </div>
                        <div className="text-gray-500">
                          Commission: ₦{reseller.commission.toLocaleString()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        {new Date(
                          reseller.registrationDate
                        ).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        Last login:{" "}
                        {reseller.lastLogin === "Never"
                          ? "Never"
                          : new Date(reseller.lastLogin).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button className="text-blue-600 hover:text-blue-900 transition-colors">
                          View
                        </button>
                        <button className="text-green-600 hover:text-green-900 transition-colors">
                          Edit
                        </button>
                        <button className="text-red-600 hover:text-red-900 transition-colors">
                          Suspend
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resellers;
